import { Scene } from 'phaser'

export class GameOverScene extends Scene {
  private background!: Phaser.GameObjects.Rectangle
  private title!: Phaser.GameObjects.Text
  private message!: Phaser.GameObjects.Text
  private stats!: Phaser.GameObjects.Text
  private buttons: Phaser.GameObjects.Container[] = []
  private victory: boolean = false

  constructor() {
    super({ key: 'GameOverScene' })
  }

  create(data?: { victory: boolean }) {
    this.victory = data?.victory || false

    const { width, height } = this.cameras.main

    // Create semi-transparent background
    this.background = this.add.rectangle(0, 0, width, height, 0x000000, 0.8)
    this.background.setOrigin(0, 0)

    // Create modal background
    const modalBg = this.add.rectangle(width / 2, height / 2, 500, 400, 0x1e293b)
    modalBg.setStrokeStyle(2, this.victory ? 0x22c55e : 0xef4444)
    modalBg.setOrigin(0.5, 0.5)

    // Title
    this.title = this.add.text(width / 2, height / 2 - 120,
      this.victory ? 'VICTORY!' : 'GAME OVER', {
      fontSize: '48px',
      fontFamily: 'Arial, sans-serif',
      color: this.victory ? '#22c55e' : '#ef4444',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    // Message
    this.message = this.add.text(width / 2, height / 2 - 50,
      this.victory ?
        'Congratulations! You have successfully defended against all waves!' :
        'The enemies have broken through your defenses.', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#94a3b8',
      align: 'center',
      wordWrap: { width: 400 }
    }).setOrigin(0.5)

    // Stats (placeholder for now)
    this.stats = this.add.text(width / 2, height / 2 + 20,
      `Waves Completed: ${this.victory ? 'All' : 'Some'}\nEnemies Defeated: 0\nCrystals Earned: 0\nTime Survived: 0:00`, {
      fontSize: '16px',
      fontFamily: 'Arial, sans-serif',
      color: '#f1f5f9',
      align: 'center'
    }).setOrigin(0.5)

    // Create buttons
    this.createButtons(width / 2, height / 2 + 120)

    // Add visual effects
    this.addVisualEffects()
  }

  private createButtons(x: number, y: number): void {
    // Claim Rewards button (only show if connected to wallet)
    const claimButton = this.add.rectangle(x, y - 40, 200, 50, 0x22c55e)
    claimButton.setStrokeStyle(2, 0x16a34a)
    claimButton.setInteractive()

    const claimText = this.add.text(x, y - 40, 'Claim Rewards', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    claimButton.on('pointerover', () => {
      claimButton.setFillStyle(0x16a34a)
      claimButton.setStrokeStyle(2, 0x15803d)
    })

    claimButton.on('pointerout', () => {
      claimButton.setFillStyle(0x22c55e)
      claimButton.setStrokeStyle(2, 0x16a34a)
    })

    claimButton.on('pointerdown', () => {
      this.claimRewards()
    })

    this.buttons.push(this.add.container(x, y - 40, [claimButton, claimText]))

    // Back to Menu button
    const menuButton = this.add.rectangle(x, y + 40, 200, 50, 0x475569)
    menuButton.setStrokeStyle(2, 0x64748b)
    menuButton.setInteractive()

    const menuText = this.add.text(x, y + 40, 'Back to Menu', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    menuButton.on('pointerover', () => {
      menuButton.setFillStyle(0x64748b)
      menuButton.setStrokeStyle(2, 0x475569)
    })

    menuButton.on('pointerout', () => {
      menuButton.setFillStyle(0x475569)
      menuButton.setStrokeStyle(2, 0x64748b)
    })

    menuButton.on('pointerdown', () => {
      this.backToMenu()
    })

    this.buttons.push(this.add.container(x, y + 40, [menuButton, menuText]))

    // Play Again button
    const playAgainButton = this.add.rectangle(x, y + 120, 200, 50, 0x3b82f6)
    playAgainButton.setStrokeStyle(2, 0x2563eb)
    playAgainButton.setInteractive()

    const playAgainText = this.add.text(x, y + 120, 'Play Again', {
      fontSize: '18px',
      fontFamily: 'Arial, sans-serif',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    playAgainButton.on('pointerover', () => {
      playAgainButton.setFillStyle(0x2563eb)
      playAgainButton.setStrokeStyle(2, 0x1d4ed8)
    })

    playAgainButton.on('pointerout', () => {
      playAgainButton.setFillStyle(0x3b82f6)
      playAgainButton.setStrokeStyle(2, 0x2563eb)
    })

    playAgainButton.on('pointerdown', () => {
      this.playAgain()
    })

    this.buttons.push(this.add.container(x, y + 120, [playAgainButton, playAgainText]))
  }

  private addVisualEffects(): void {
    const { width, height } = this.cameras.main

    if (this.victory) {
      // Add celebration particles for victory
      for (let i = 0; i < 30; i++) {
        const x = Math.random() * width
        const y = Math.random() * height
        const color = [0x22c55e, 0x3b82f6, 0xf59e0b, 0xef4444][Math.floor(Math.random() * 4)]
        const size = Math.random() * 4 + 2

        const particle = this.add.circle(x, y, size, color)

        this.tweens.add({
          targets: particle,
          y: -10,
          alpha: 0,
          duration: Math.random() * 2000 + 1000,
          ease: 'Linear',
          repeat: -1,
          delay: Math.random() * 1000
        })
      }
    } else {
      // Add somber effects for game over
      for (let i = 0; i < 15; i++) {
        const x = Math.random() * width
        const y = Math.random() * height

        const particle = this.add.circle(x, y, 2, 0xef4444, 0.3)

        this.tweens.add({
          targets: particle,
          alpha: { from: 0.3, to: 0 },
          duration: 2000,
          ease: 'Linear',
          repeat: -1,
          yoyo: true
        })
      }
    }
  }

  private claimRewards(): void {
    console.log('Claiming rewards...')
    // This will open the claim modal in React
  }

  private backToMenu(): void {
    this.game.events.emit('resetGame')
    this.scene.stop()
  }

  private playAgain(): void {
    // Restart the current game with same settings
    this.scene.start('PlayingScene')
  }
}